import React, { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  FileText, ArrowLeft, Download, CheckCircle2, Clock, Truck,
  Receipt, Euro, TrendingUp, HardHat, Package, Calculator,
  ChevronRight, Save, X, AlertTriangle, Edit, User, MapPin,
  Loader2, Percent, Briefcase
} from 'lucide-react';

const STAGES = [
  { id: 'devis', label: 'Devis', icon: FileText },
  { id: 'accepte', label: 'Accepté', icon: CheckCircle2 },
  { id: 'bon_commande', label: 'Bon de commande', icon: Receipt },
  { id: 'chantier', label: 'Chantier', icon: HardHat },
  { id: 'facture', label: 'Facturé', icon: Euro },
];

export default function DevisLifecycleView({ devisId, onBack }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [sousTraitants, setSousTraitants] = useState([]);
  const [editing, setEditing] = useState(null); // 'bon_commande' | 'chantier' | 'facture'
  const [saving, setSaving] = useState(false);

  // Form states
  const [bonCommande, setBonCommande] = useState({ reference: '', date: '', notes: '' });
  const [chantierForm, setChantierForm] = useState({
    sous_traitant_id: '', sous_traitant_nom: '',
    cout_main_oeuvre: '', cout_materiaux: '', cout_autres: '',
    notes_chantier: ''
  });
  const [factureForm, setFactureForm] = useState({ numero: '', montant: '', date: '' });

  useEffect(() => {
    loadLifecycle();
    loadSousTraitants();
  }, [devisId]);

  const loadLifecycle = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/devis/${devisId}/lifecycle`);
      setData(res.data);
      // Pre-fill forms
      const lc = res.data.lifecycle || {};
      if (lc.bon_commande) setBonCommande(lc.bon_commande);
      setChantierForm({
        sous_traitant_id: lc.sous_traitant_id || '',
        sous_traitant_nom: lc.sous_traitant_nom || '',
        cout_main_oeuvre: lc.cout_main_oeuvre || '',
        cout_materiaux: lc.cout_materiaux || '',
        cout_autres: lc.cout_autres || '',
        notes_chantier: lc.notes_chantier || ''
      });
      setFactureForm({
        numero: lc.facture_numero || '',
        montant: lc.facture_montant || '',
        date: lc.facture_date || ''
      });
    } catch (e) {
      console.error('Error loading lifecycle:', e);
    }
    setLoading(false);
  };

  const loadSousTraitants = async () => {
    try {
      const res = await api.get('/sous-traitants-selector');
      setSousTraitants(res.data.sous_traitants || []);
    } catch (e) {
      console.error(e);
    }
  };

  const handleSave = async (section) => {
    setSaving(true);
    try {
      let payload = {};
      if (section === 'bon_commande') {
        payload = { bon_commande: { ...bonCommande, saved: true } };
      } else if (section === 'chantier') {
        payload = {
          sous_traitant_id: chantierForm.sous_traitant_id,
          sous_traitant_nom: chantierForm.sous_traitant_nom,
          cout_main_oeuvre: parseFloat(chantierForm.cout_main_oeuvre) || 0,
          cout_materiaux: parseFloat(chantierForm.cout_materiaux) || 0,
          cout_autres: parseFloat(chantierForm.cout_autres) || 0,
          notes_chantier: chantierForm.notes_chantier
        };
      } else if (section === 'facture') {
        payload = {
          facture_numero: factureForm.numero,
          facture_montant: parseFloat(factureForm.montant) || 0,
          facture_date: factureForm.date
        };
      }
      await api.patch(`/devis/${devisId}/lifecycle`, payload);
      await loadLifecycle();
      setEditing(null);
    } catch (e) {
      console.error('Error saving:', e);
    }
    setSaving(false);
  };

  const handleSelectST = (st) => {
    setChantierForm({
      ...chantierForm,
      sous_traitant_id: st.sous_traitant_id,
      sous_traitant_nom: st.nom
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  if (!data) return null;

  const { devis, lifecycle, financials, sous_traitant } = data;
  const stageIdx = STAGES.findIndex(s => s.id === lifecycle.stage);

  return (
    <div className="space-y-6" data-testid="devis-lifecycle-view">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={onBack} className="p-2" data-testid="lifecycle-back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h2 className="text-2xl font-bold text-slate-900">{devis.devis_id}</h2>
            <p className="text-slate-500">{devis.project_title || 'Sans titre'}</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/devis/${devis.devis_id}/pdf`, '_blank')}
            className="border-green-300 text-green-700 hover:bg-green-50"
            data-testid="download-pdf-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            Télécharger PDF
          </Button>
        </div>
      </div>

      {/* Pipeline / Timeline */}
      <div className="bg-white rounded-xl p-6 border border-slate-200">
        <div className="flex items-center justify-between relative">
          {/* Progress line */}
          <div className="absolute top-5 left-0 right-0 h-0.5 bg-slate-200 z-0" />
          <div
            className="absolute top-5 left-0 h-0.5 bg-green-500 z-0 transition-all duration-500"
            style={{ width: `${Math.max(0, stageIdx) / (STAGES.length - 1) * 100}%` }}
          />
          {STAGES.map((stage, idx) => {
            const Icon = stage.icon;
            const isActive = idx <= stageIdx;
            const isCurrent = stage.id === lifecycle.stage;
            return (
              <div key={stage.id} className="flex flex-col items-center z-10 relative">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all ${
                  isCurrent ? 'bg-green-500 border-green-500 text-white scale-110 shadow-lg shadow-green-200' :
                  isActive ? 'bg-green-500 border-green-500 text-white' :
                  'bg-white border-slate-300 text-slate-400'
                }`}>
                  <Icon className="w-5 h-5" />
                </div>
                <span className={`mt-2 text-xs font-medium ${
                  isCurrent ? 'text-green-700' : isActive ? 'text-green-600' : 'text-slate-400'
                }`}>
                  {stage.label}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Financial Summary Cards */}
      <div className="grid grid-cols-5 gap-3">
        <div className="bg-white rounded-xl p-4 border border-slate-200">
          <p className="text-xs text-slate-500 mb-1">Montant devis HT</p>
          <p className="text-xl font-bold text-slate-900">{financials.montant_devis_ht?.toLocaleString('fr-FR')}€</p>
        </div>
        <div className="bg-white rounded-xl p-4 border border-slate-200">
          <p className="text-xs text-slate-500 mb-1">Main d'oeuvre S/T</p>
          <p className="text-xl font-bold text-orange-600">{financials.cout_main_oeuvre?.toLocaleString('fr-FR') || '0'}€</p>
        </div>
        <div className="bg-white rounded-xl p-4 border border-slate-200">
          <p className="text-xs text-slate-500 mb-1">Matériaux</p>
          <p className="text-xl font-bold text-blue-600">{financials.cout_materiaux?.toLocaleString('fr-FR') || '0'}€</p>
        </div>
        <div className="bg-white rounded-xl p-4 border border-slate-200">
          <p className="text-xs text-slate-500 mb-1">Coût total réel</p>
          <p className="text-xl font-bold text-red-600">{financials.cout_total_reel?.toLocaleString('fr-FR') || '0'}€</p>
        </div>
        <div className={`rounded-xl p-4 border-2 ${
          financials.marge_nette >= 0
            ? 'bg-green-50 border-green-300'
            : 'bg-red-50 border-red-300'
        }`}>
          <p className="text-xs text-slate-500 mb-1">Marge nette</p>
          <div className="flex items-baseline gap-2">
            <p className={`text-xl font-bold ${financials.marge_nette >= 0 ? 'text-green-700' : 'text-red-700'}`}>
              {financials.marge_nette?.toLocaleString('fr-FR')}€
            </p>
            <span className={`text-sm font-medium ${financials.marge_percent >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              ({financials.marge_percent}%)
            </span>
          </div>
        </div>
      </div>

      {/* Devis details */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="p-5 border-b border-slate-100 flex items-center justify-between">
          <h3 className="font-semibold text-slate-900 flex items-center gap-2">
            <FileText className="w-4 h-4 text-blue-500" />
            Détails du devis
          </h3>
          <span className={`px-3 py-1 rounded-full text-xs font-medium ${
            devis.statut === 'Accepté' ? 'bg-green-100 text-green-700' :
            devis.statut === 'Refusé' ? 'bg-red-100 text-red-700' :
            'bg-amber-100 text-amber-700'
          }`}>
            {devis.statut || 'Brouillon'}
          </span>
        </div>
        <div className="p-5">
          <div className="grid grid-cols-3 gap-4 mb-4">
            <div>
              <p className="text-xs text-slate-400">Client</p>
              <p className="font-medium text-slate-900">{devis.client_info?.nom}</p>
            </div>
            <div>
              <p className="text-xs text-slate-400">Adresse chantier</p>
              <p className="font-medium text-slate-900">{devis.chantier_adresse || '-'}</p>
            </div>
            <div>
              <p className="text-xs text-slate-400">Date création</p>
              <p className="font-medium text-slate-900">
                {devis.date_creation ? new Date(devis.date_creation).toLocaleDateString('fr-FR') : '-'}
              </p>
            </div>
          </div>
          {/* Line items (div-grid pour éviter <span> injectés dans <tbody>) */}
          <div className="border rounded-lg overflow-hidden">
            <div className="w-full text-sm" role="table">
              <div role="row" className="grid grid-cols-[1fr_60px_90px_100px] bg-slate-50">
                <div role="columnheader" className="text-left p-3 text-slate-600 font-medium">Description</div>
                <div role="columnheader" className="text-center p-3 text-slate-600 font-medium">Qté</div>
                <div role="columnheader" className="text-right p-3 text-slate-600 font-medium">PU HT</div>
                <div role="columnheader" className="text-right p-3 text-slate-600 font-medium">Total HT</div>
              </div>
              <div role="rowgroup">
                {devis.lignes?.map((l, i) => (
                  <div key={i} role="row" className="grid grid-cols-[1fr_60px_90px_100px] items-center border-t border-slate-100">
                    <div role="cell" className="p-3 text-slate-700">{l.description?.substring(0, 100)}{l.description?.length > 100 ? '...' : ''}</div>
                    <div role="cell" className="p-3 text-center text-slate-600">{l.quantite} {l.unite}</div>
                    <div role="cell" className="p-3 text-right text-slate-600">{l.prix_unitaire?.toLocaleString('fr-FR')}€</div>
                    <div role="cell" className="p-3 text-right font-medium text-slate-900">{(l.total_ht || l.prix_unitaire * l.quantite)?.toLocaleString('fr-FR')}€</div>
                  </div>
                ))}
              </div>
              <div role="rowgroup" className="bg-slate-50 font-medium">
                <div role="row" className="grid grid-cols-[1fr_60px_90px_100px] items-center border-t">
                  <div role="cell" className="p-3 text-right text-slate-600 col-span-3" style={{ gridColumn: 'span 3' }}>Total HT</div>
                  <div role="cell" className="p-3 text-right text-slate-900">{financials.montant_devis_ht?.toLocaleString('fr-FR')}€</div>
                </div>
                <div role="row" className="grid grid-cols-[1fr_60px_90px_100px] items-center">
                  <div role="cell" className="p-3 text-right text-slate-600" style={{ gridColumn: 'span 3' }}>TTC</div>
                  <div role="cell" className="p-3 text-right text-slate-900 font-bold">{financials.montant_devis_ttc?.toLocaleString('fr-FR')}€</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bon de commande */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="p-5 border-b border-slate-100 flex items-center justify-between">
          <h3 className="font-semibold text-slate-900 flex items-center gap-2">
            <Receipt className="w-4 h-4 text-purple-500" />
            Bon de commande
          </h3>
          {lifecycle.bon_commande ? (
            <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">Reçu</span>
          ) : (
            <Button size="sm" variant="outline" onClick={() => setEditing('bon_commande')} data-testid="edit-bon-commande-btn">
              <Edit className="w-3 h-3 mr-1" /> Renseigner
            </Button>
          )}
        </div>
        {editing === 'bon_commande' ? (
          <div className="p-5 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Référence BC</label>
                <Input value={bonCommande.reference} onChange={e => setBonCommande({...bonCommande, reference: e.target.value})} placeholder="N° bon de commande" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Date réception</label>
                <Input type="date" value={bonCommande.date} onChange={e => setBonCommande({...bonCommande, date: e.target.value})} />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Notes</label>
              <Input value={bonCommande.notes} onChange={e => setBonCommande({...bonCommande, notes: e.target.value})} placeholder="Notes..." />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setEditing(null)}>Annuler</Button>
              <Button onClick={() => handleSave('bon_commande')} disabled={saving} className="bg-purple-600 hover:bg-purple-700">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
                Enregistrer
              </Button>
            </div>
          </div>
        ) : lifecycle.bon_commande ? (
          <div className="p-5">
            <div className="grid grid-cols-3 gap-4">
              <div>
                <p className="text-xs text-slate-400">Référence</p>
                <p className="font-medium text-slate-900">{lifecycle.bon_commande.reference || '-'}</p>
              </div>
              <div>
                <p className="text-xs text-slate-400">Date réception</p>
                <p className="font-medium text-slate-900">{lifecycle.bon_commande.date ? new Date(lifecycle.bon_commande.date).toLocaleDateString('fr-FR') : '-'}</p>
              </div>
              <div>
                <p className="text-xs text-slate-400">Notes</p>
                <p className="text-slate-700">{lifecycle.bon_commande.notes || '-'}</p>
              </div>
            </div>
            <Button size="sm" variant="ghost" className="mt-3 text-slate-500" onClick={() => setEditing('bon_commande')}>
              <Edit className="w-3 h-3 mr-1" /> Modifier
            </Button>
          </div>
        ) : (
          <div className="p-8 text-center text-slate-400">
            <Receipt className="w-8 h-8 mx-auto mb-2 opacity-30" />
            <p className="text-sm">Aucun bon de commande enregistré</p>
          </div>
        )}
      </div>

      {/* Chantier - Sous-traitant & Coûts */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="p-5 border-b border-slate-100 flex items-center justify-between">
          <h3 className="font-semibold text-slate-900 flex items-center gap-2">
            <HardHat className="w-4 h-4 text-amber-500" />
            Chantier - Sous-traitant & Coûts
          </h3>
          {editing !== 'chantier' && (
            <Button size="sm" variant="outline" onClick={() => setEditing('chantier')} data-testid="edit-chantier-btn">
              <Edit className="w-3 h-3 mr-1" /> {lifecycle.sous_traitant_id ? 'Modifier' : 'Renseigner'}
            </Button>
          )}
        </div>
        {editing === 'chantier' ? (
          <div className="p-5 space-y-5">
            {/* Sous-traitant selector */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Sous-traitant assigné</label>
              {sousTraitants.length > 0 ? (
                <div className="grid grid-cols-3 gap-2">
                  {sousTraitants.map(st => (
                    <div
                      key={st.sous_traitant_id}
                      onClick={() => handleSelectST(st)}
                      className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                        chantierForm.sous_traitant_id === st.sous_traitant_id
                          ? 'border-amber-500 bg-amber-50'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <p className="font-medium text-slate-900">{st.nom}</p>
                      <p className="text-xs text-slate-500">{st.specialite}</p>
                      {st.tarif_journalier && <p className="text-xs text-amber-600 mt-1">{st.tarif_journalier}€/jour</p>}
                    </div>
                  ))}
                </div>
              ) : (
                <Input
                  value={chantierForm.sous_traitant_nom}
                  onChange={e => setChantierForm({...chantierForm, sous_traitant_nom: e.target.value})}
                  placeholder="Nom du sous-traitant"
                />
              )}
            </div>
            {/* Costs */}
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Coût main d'oeuvre S/T (€)</label>
                <Input
                  type="number" value={chantierForm.cout_main_oeuvre}
                  onChange={e => setChantierForm({...chantierForm, cout_main_oeuvre: e.target.value})}
                  placeholder="0"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Coût matériaux (€)</label>
                <Input
                  type="number" value={chantierForm.cout_materiaux}
                  onChange={e => setChantierForm({...chantierForm, cout_materiaux: e.target.value})}
                  placeholder="0"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Autres coûts (€)</label>
                <Input
                  type="number" value={chantierForm.cout_autres}
                  onChange={e => setChantierForm({...chantierForm, cout_autres: e.target.value})}
                  placeholder="0"
                />
              </div>
            </div>
            {/* Live margin preview */}
            {(() => {
              const mo = parseFloat(chantierForm.cout_main_oeuvre) || 0;
              const mat = parseFloat(chantierForm.cout_materiaux) || 0;
              const autres = parseFloat(chantierForm.cout_autres) || 0;
              const total = mo + mat + autres;
              const marge = financials.montant_devis_ht - total;
              const pct = financials.montant_devis_ht > 0 ? (marge / financials.montant_devis_ht * 100).toFixed(1) : 0;
              return (
                <div className={`p-4 rounded-lg border-2 ${marge >= 0 ? 'bg-green-50 border-green-300' : 'bg-red-50 border-red-300'}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-6">
                      <div>
                        <p className="text-xs text-slate-500">Coût total</p>
                        <p className="text-lg font-bold text-slate-900">{total.toLocaleString('fr-FR')}€</p>
                      </div>
                      <ChevronRight className="w-4 h-4 text-slate-300" />
                      <div>
                        <p className="text-xs text-slate-500">Marge nette estimée</p>
                        <p className={`text-lg font-bold ${marge >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                          {marge.toLocaleString('fr-FR')}€ ({pct}%)
                        </p>
                      </div>
                    </div>
                    <Calculator className={`w-6 h-6 ${marge >= 0 ? 'text-green-400' : 'text-red-400'}`} />
                  </div>
                </div>
              );
            })()}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Notes chantier</label>
              <Textarea
                value={chantierForm.notes_chantier}
                onChange={e => setChantierForm({...chantierForm, notes_chantier: e.target.value})}
                placeholder="Notes sur le chantier..."
                rows={2}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setEditing(null)}>Annuler</Button>
              <Button onClick={() => handleSave('chantier')} disabled={saving} className="bg-amber-500 hover:bg-amber-600">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
                Enregistrer
              </Button>
            </div>
          </div>
        ) : (lifecycle.sous_traitant_id || financials.cout_total_reel > 0) ? (
          <div className="p-5">
            <div className="grid grid-cols-2 gap-6">
              {/* Sous-traitant info */}
              <div>
                <p className="text-xs text-slate-400 mb-2">Sous-traitant assigné</p>
                {sous_traitant ? (
                  <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                    <p className="font-semibold text-slate-900">{sous_traitant.nom}</p>
                    <p className="text-sm text-slate-500">{sous_traitant.specialite}</p>
                    {sous_traitant.telephone && <p className="text-sm text-slate-500 mt-1">{sous_traitant.telephone}</p>}
                  </div>
                ) : lifecycle.sous_traitant_nom ? (
                  <p className="font-medium text-slate-900">{lifecycle.sous_traitant_nom}</p>
                ) : (
                  <p className="text-slate-400">Non assigné</p>
                )}
              </div>
              {/* Costs breakdown */}
              <div>
                <p className="text-xs text-slate-400 mb-2">Ventilation des coûts</p>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm text-slate-600">Main d'oeuvre S/T</span>
                    <span className="font-medium text-slate-900">{financials.cout_main_oeuvre?.toLocaleString('fr-FR')}€</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-slate-600">Matériaux</span>
                    <span className="font-medium text-slate-900">{financials.cout_materiaux?.toLocaleString('fr-FR')}€</span>
                  </div>
                  {financials.cout_autres > 0 && (
                    <div className="flex justify-between">
                      <span className="text-sm text-slate-600">Autres</span>
                      <span className="font-medium text-slate-900">{financials.cout_autres?.toLocaleString('fr-FR')}€</span>
                    </div>
                  )}
                  <div className="flex justify-between pt-2 border-t border-slate-200 font-semibold">
                    <span className="text-slate-900">Total coûts</span>
                    <span className="text-red-600">{financials.cout_total_reel?.toLocaleString('fr-FR')}€</span>
                  </div>
                </div>
              </div>
            </div>
            {lifecycle.notes_chantier && (
              <p className="mt-3 text-sm text-slate-500 italic">"{lifecycle.notes_chantier}"</p>
            )}
          </div>
        ) : (
          <div className="p-8 text-center text-slate-400">
            <HardHat className="w-8 h-8 mx-auto mb-2 opacity-30" />
            <p className="text-sm">Aucune info chantier enregistrée</p>
          </div>
        )}
      </div>

      {/* Facture */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="p-5 border-b border-slate-100 flex items-center justify-between">
          <h3 className="font-semibold text-slate-900 flex items-center gap-2">
            <Euro className="w-4 h-4 text-green-500" />
            Facture
          </h3>
          {editing !== 'facture' && (
            <Button size="sm" variant="outline" onClick={() => setEditing('facture')} data-testid="edit-facture-btn">
              <Edit className="w-3 h-3 mr-1" /> {lifecycle.facture_numero ? 'Modifier' : 'Renseigner'}
            </Button>
          )}
        </div>
        {editing === 'facture' ? (
          <div className="p-5 space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">N° facture</label>
                <Input value={factureForm.numero} onChange={e => setFactureForm({...factureForm, numero: e.target.value})} placeholder="FAC-2026-001" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Montant (€)</label>
                <Input type="number" value={factureForm.montant} onChange={e => setFactureForm({...factureForm, montant: e.target.value})} placeholder="0" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Date</label>
                <Input type="date" value={factureForm.date} onChange={e => setFactureForm({...factureForm, date: e.target.value})} />
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setEditing(null)}>Annuler</Button>
              <Button onClick={() => handleSave('facture')} disabled={saving} className="bg-green-600 hover:bg-green-700">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
                Enregistrer
              </Button>
            </div>
          </div>
        ) : lifecycle.facture_numero ? (
          <div className="p-5">
            <div className="grid grid-cols-3 gap-4">
              <div>
                <p className="text-xs text-slate-400">Numéro</p>
                <p className="font-medium text-slate-900">{lifecycle.facture_numero}</p>
              </div>
              <div>
                <p className="text-xs text-slate-400">Montant</p>
                <p className="font-medium text-slate-900">{lifecycle.facture_montant?.toLocaleString('fr-FR')}€</p>
              </div>
              <div>
                <p className="text-xs text-slate-400">Date</p>
                <p className="font-medium text-slate-900">{lifecycle.facture_date ? new Date(lifecycle.facture_date).toLocaleDateString('fr-FR') : '-'}</p>
              </div>
            </div>
          </div>
        ) : (
          <div className="p-8 text-center text-slate-400">
            <Euro className="w-8 h-8 mx-auto mb-2 opacity-30" />
            <p className="text-sm">Aucune facture enregistrée</p>
          </div>
        )}
      </div>
    </div>
  );
}
